import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import ReportImage from "./ReportImage";
import NoImagePlaceholder from "./NoImagePlaceholder";

interface ImageCarouselProps {
  images: string[];
  showPreviews?: boolean;
}

const ImageCarousel: React.FC<ImageCarouselProps> = memo(
  ({ images, showPreviews = true }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showLightbox, setShowLightbox] = useState(false);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const carouselRef = useRef<HTMLDivElement>(null);
    const thumbnailsRef = useRef<HTMLDivElement>(null);

    const nextImage = useCallback(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, [images.length]);

    const prevImage = useCallback(() => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }, [images.length]);

    const goToImage = useCallback((index: number) => {
      setCurrentIndex(index);
    }, []);

    // Scroll thumbnail into view when current index changes
    const scrollThumbnailIntoView = useCallback(() => {
      if (!thumbnailsRef.current) return;

      const thumbnailElement = thumbnailsRef.current.children[
        currentIndex
      ] as HTMLElement;
      if (thumbnailElement) {
        // Check if the element is already in view
        const containerRect = thumbnailsRef.current.getBoundingClientRect();
        const elementRect = thumbnailElement.getBoundingClientRect();

        // Check if the element is outside the visible area
        const isOutsideLeft = elementRect.left < containerRect.left;
        const isOutsideRight = elementRect.right > containerRect.right;

        if (isOutsideLeft || isOutsideRight) {
          thumbnailElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }
    }, [currentIndex]);

    // Effect to scroll thumbnail when currentIndex changes
    useEffect(() => {
      scrollThumbnailIntoView();
    }, [currentIndex, scrollThumbnailIntoView]);

    const openLightbox = useCallback(() => {
      setShowLightbox(true);
    }, []);

    const closeLightbox = useCallback(() => {
      setShowLightbox(false);
    }, []);

    // Keyboard navigation
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (showLightbox) {
          switch (e.key) {
            case "ArrowLeft":
              e.preventDefault();
              prevImage();
              break;
            case "ArrowRight":
              e.preventDefault();
              nextImage();
              break;
            case "Escape":
              e.preventDefault();
              closeLightbox();
              break;
          }
        }
      };

      if (showLightbox) {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "auto";
      };
    }, [showLightbox, nextImage, prevImage, closeLightbox]);

    if (images.length === 0) {
      return <NoImagePlaceholder />;
    }

    // Touch handlers
    const handleTouchStart = (e: React.TouchEvent) => {
      setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (!touchStart || !touchEnd) return;

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > 50;
      const isRightSwipe = distance < -50;

      if (isLeftSwipe && images.length > 1) {
        nextImage();
      }
      if (isRightSwipe && images.length > 1) {
        prevImage();
      }
    };

    return (
      <>
        <div className="image-carousel" ref={carouselRef}>
          {/* Main image display */}
          <div className="carousel-main-image">
            <div
              className="carousel-image-container"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={openLightbox}
            >
              <ReportImage
                src={images[currentIndex]}
                alt={`Report image ${currentIndex + 1}`}
                index={currentIndex}
              />
              <div className="image-overlay">
                <span className="expand-icon">🔍</span>
              </div>
            </div>

            {/* Navigation arrows */}
            {images.length > 1 && (
              <>
                <button
                  className="carousel-nav prev"
                  onClick={prevImage}
                  aria-label="Previous image"
                >
                  &#8249;
                </button>
                <button
                  className="carousel-nav next"
                  onClick={nextImage}
                  aria-label="Next image"
                >
                  &#8250;
                </button>
              </>
            )}

            {/* Image counter */}
            {images.length > 1 && (
              <div className="image-counter">
                {currentIndex + 1} / {images.length}
              </div>
            )}
          </div>

          {/* Thumbnail navigation */}
          {images.length > 1 && showPreviews && (
            <div className="carousel-thumbnails" ref={thumbnailsRef}>
              {images.map((image, index) => (
                <button
                  key={index}
                  className={`thumbnail ${
                    index === currentIndex ? "active" : ""
                  }`}
                  onClick={() => goToImage(index)}
                  aria-label={`Go to image ${index + 1}`}
                >
                  <img src={image} alt={`Thumbnail ${index + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Lightbox */}
        {showLightbox && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <div
              className="lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="lightbox-close" onClick={closeLightbox}>
                ×
              </button>

              <div className="lightbox-image-container">
                <img
                  src={images[currentIndex]}
                  alt={`Report ${currentIndex + 1}`}
                  className="lightbox-image"
                />
              </div>

              {images.length > 1 && (
                <>
                  <button
                    className="lightbox-nav prev"
                    onClick={prevImage}
                    aria-label="Previous image"
                  >
                    &#8249;
                  </button>
                  <button
                    className="lightbox-nav next"
                    onClick={nextImage}
                    aria-label="Next image"
                  >
                    &#8250;
                  </button>

                  <div className="lightbox-counter">
                    {currentIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  }
);

ImageCarousel.displayName = "ImageCarousel";

export default ImageCarousel;
